import type { InteractionSnapshot } from "./interaction-controller.js";

export class InteractionHud {
  private readonly name: HTMLElement;
  private readonly button: HTMLButtonElement;
  private readonly buttonLabel: HTMLElement;
  private readonly progress: HTMLElement;
  private readonly progressFill: HTMLElement;

  constructor(
    private readonly root: HTMLElement,
    private readonly feedback: HTMLElement,
    private readonly indicator: HTMLElement,
  ) {
    this.name = required(root, "#interaction-name");
    this.button = required(root, "#interaction-action");
    this.buttonLabel = required(root, "#interaction-action-label");
    this.progress = required(root, "#interaction-progress");
    this.progressFill = required(root, "#interaction-progress-fill");
  }

  render(snapshot: InteractionSnapshot, viewProjection: Float32Array, width = innerWidth, height = innerHeight): void {
    const active = snapshot.state === "STARTING" || snapshot.state === "ACTIVE";
    const transient = snapshot.state === "COMPLETED"
      || snapshot.state === "CANCELLED"
      || snapshot.state === "REJECTED";
    this.root.hidden = !snapshot.targetAvailable || transient;
    this.root.dataset.state = snapshot.state.toLowerCase();
    this.name.textContent = snapshot.targetId ? displayName(snapshot) : "";
    this.buttonLabel.textContent = active
      ? snapshot.cancelling ? "CANCELLING" : activeLabel(snapshot.actionLabel)
      : snapshot.actionLabel ?? "INTERACT";
    this.button.setAttribute("aria-label", `${active ? "Release to cancel" : "Hold to"} ${(snapshot.actionLabel ?? "interact").toLowerCase()}`);
    this.progress.hidden = !active;
    this.progressFill.style.transform = `scaleX(${Math.max(0, Math.min(1, snapshot.progress))})`;
    this.progress.setAttribute("aria-valuenow", Math.round(snapshot.progress * 100).toString());

    this.feedback.hidden = snapshot.feedback === null;
    this.feedback.textContent = snapshot.feedback ?? "";
    this.positionIndicator(snapshot, viewProjection, width, height);
  }

  private positionIndicator(snapshot: InteractionSnapshot, matrix: Float32Array, width: number, height: number): void {
    const anchor = snapshot.anchor;
    if (!anchor || !snapshot.targetAvailable) {
      this.indicator.hidden = true;
      return;
    }
    const x = anchor[0];
    const y = anchor[1];
    const z = anchor[2];
    const clipX = matrix[0]! * x + matrix[4]! * y + matrix[8]! * z + matrix[12]!;
    const clipY = matrix[1]! * x + matrix[5]! * y + matrix[9]! * z + matrix[13]!;
    const clipW = matrix[3]! * x + matrix[7]! * y + matrix[11]! * z + matrix[15]!;
    if (clipW <= 0.001) {
      this.indicator.hidden = true;
      return;
    }
    const screenX = (clipX / clipW * 0.5 + 0.5) * width;
    const screenY = (0.5 - clipY / clipW * 0.5) * height;
    if (screenX < -24 || screenY < -24 || screenX > width + 24 || screenY > height + 24) {
      this.indicator.hidden = true;
      return;
    }
    this.indicator.hidden = false;
    this.indicator.dataset.blocked = snapshot.lineOfSight ? "false" : "true";
    this.indicator.style.transform = `translate3d(${screenX.toFixed(1)}px,${screenY.toFixed(1)}px,0)`;
  }
}

function required<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing interaction UI element: ${selector}`);
  return element;
}

function displayName(snapshot: InteractionSnapshot): string {
  return snapshot.displayName
    ?? (snapshot.targetType ?? "WORLD OBJECT").replaceAll("_", " ").toUpperCase();
}

function activeLabel(label: string | null): string {
  if (label === "OPEN") return "OPENING";
  if (label === "INSPECT") return "INSPECTING";
  return label ? `${label}…` : "WORKING";
}
